// Component: ActionHeader
// Description: This component is used to render publish/save actions for service.

import React from 'react';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { useAuth } from 'lib/Auth';
import Button from 'components/UI/Button';
import { GENERAL_CONSTANTS } from 'constants/general';
import { Main } from 'styles/themeColors';
import { isOfmpAdmin, isVendorAdmin, isVendorOnly } from 'data/User';
import { SERVICE_CONSTANTS, ADDON_STATUS_CODE } from 'constants/service';
import PageHeading from 'components/UI/PageHeading';
import { getAddonPillTheme, getMappedAddonDisplayState } from 'utils/addOn';
import Pill from 'components/UI/Pill';
import { PermissionCode } from 'data/UserPermissions';


interface Props {
    onSave: (event: any) => void;
    onPublish?: (event: any) => void;
    onUnPublish?: (event: any) => void;
    onDelete?: (event: any) => void;
    onCancel?: (event: any) => void;
    isValid?: boolean;
    serviceStatus?: string;

}

const ServiceFormHeader: React.FC<Props> = ({
    onSave, onPublish, onUnPublish, onDelete, onCancel, isValid, serviceStatus
}: Props) => {

    const { user } = useAuth();
    const _isVendorOnly = isVendorOnly(user!);
    const { displayState } = getMappedAddonDisplayState(serviceStatus!);
    const pillTheme = getAddonPillTheme(serviceStatus!);
    const pill = <Pill margin="0 0 0 0" theme={pillTheme} bgColor={pillTheme} size="xsPlus" padding="4px">{displayState}</Pill>;


    return (
        <FlexboxContainerComponent justifyContent='space-between' width='100%' alignItems='center'>

            <FlexboxContainerComponent>
                <FlexboxContainerComponent alignItems='start' gap='12px'>

                    <PageHeading>{SERVICE_CONSTANTS.NEW_SERVICE}</PageHeading>
                    <div>{pill}</div>
                </FlexboxContainerComponent>

            </FlexboxContainerComponent>
            <FlexboxContainerComponent gap='8px'>

                {/* Show delete button only if service is in draft state */}
                {serviceStatus === ADDON_STATUS_CODE.DRAFT &&
                    <Button
                        onClick={onDelete}
                        bgColor={Main.white}
                        borderColor={Main.error}
                        isGhost
                        color={Main.error}
                        permissionCode={PermissionCode.ADDONS_CREATE}
                    >
                        {GENERAL_CONSTANTS.DELETE}
                    </Button>
                }

                <Button
                    onClick={onCancel}
                    bgColor={Main.white}
                    borderColor={Main.gray}
                    isGhost
                    color={Main.gray}
                >
                    {GENERAL_CONSTANTS.CLOSE}
                </Button>

                {
                    _isVendorOnly ? null :
                        <>
                            {serviceStatus !== ADDON_STATUS_CODE.PUBLISHED && <Button
                                onClick={onPublish}
                                bgColor={Main.gray}
                                borderColor={Main.gray}
                                disabled={!isValid}  // Disable the button user is not ofmp admin
                                permissionCode={PermissionCode.ADDONS_PUBLISH}
                            >
                                {GENERAL_CONSTANTS.PUBLISH}
                            </Button>
                            }

                            {serviceStatus === ADDON_STATUS_CODE.PUBLISHED && <Button
                                onClick={onUnPublish}
                                bgColor={Main.error}
                                borderColor={Main.error}
                                disabled={!isValid} // Disable the button user is not ofmp admin
                                permissionCode={PermissionCode.ADDONS_PUBLISH}
                            >
                                {GENERAL_CONSTANTS.UNPUBLISH}
                            </Button>
                            }
                        </>
                }

                <Button
                    onClick={onSave}
                    bgColor={Main.brandDark}
                    borderColor={Main.brandDark}
                    type='submit'
                    key={'save'}
                    disabled={serviceStatus === ADDON_STATUS_CODE.PUBLISHED && !isValid}
                    permissionCode={serviceStatus === ADDON_STATUS_CODE.PUBLISHED ? PermissionCode.ADDONS_PUBLISH : PermissionCode.ADDONS_CREATE}
                >
                    {!_isVendorOnly && serviceStatus === ADDON_STATUS_CODE.PUBLISHED ? GENERAL_CONSTANTS.PUBLISH : GENERAL_CONSTANTS.SAVE}
                </Button>
            </FlexboxContainerComponent>
        </FlexboxContainerComponent>
    );
}

export default (ServiceFormHeader);